import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'

const StyledRangeInput = styled.input<{ size: number }>`
  -webkit-appearance: none; /* Hides the slider so that custom slider can be made */
  width: 100%; /* Specific width is required for Firefox. */
  background: transparent; /* Otherwise white in Chrome */
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &::-moz-focus-outer {
    border: 0;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;
    background-color: ${({ theme }) => theme.blue1};
    border-radius: 100%;
    border: none;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.bg1};

    &:hover,
    &:focus {
      box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.08), 0px 16px 24px rgba(0, 0, 0, 0.06),
        0px 24px 32px rgba(0, 0, 0, 0.04);
    }
  }

  &::-moz-range-thumb {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;
    background-color: #565a69;
    border-radius: 100%;
    border: none;
    color: ${({ theme }) => theme.bg1};

    &:hover,
    &:focus {
      box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.08), 0px 16px 24px rgba(0, 0, 0, 0.06),
        0px 24px 32px rgba(0, 0, 0, 0.04);
    }
  }

  &::-ms-thumb {
    height: ${({ size }) => size}px;
    width: ${({ size }) => size}px;
    background-color: #565a69;
    border-radius: 100%;
    color: ${({ theme }) => theme.bg1};

    &:hover,
    &:focus {
      box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.08), 0px 16px 24px rgba(0, 0, 0, 0.06),
        0px 24px 32px rgba(0, 0, 0, 0.04);
    }
  }

  &::-webkit-slider-runnable-track {
    background: linear-gradient(90deg, ${({ theme }) => theme.blue1}, ${({ theme }) => theme.blue2});
    height: 2px;
  }

  &::-moz-range-track {
    background: linear-gradient(90deg, ${({ theme }) => theme.bg5}, ${({ theme }) => theme.bg3});
    height: 2px;
  }

  &::-ms-track {
    width: 100%;
    border-color: transparent;
    color: transparent;

    background: ${({ theme }) => theme.bg5};
    height: 2px;
  }
  &::-ms-fill-lower {
    background: ${({ theme }) => theme.bg5};
  }
  &::-ms-fill-upper {
    background: ${({ theme }) => theme.bg3};
  }
`

interface InputSliderProps {
  value: number
  onChange: (value: number) => void
  step?: number
  min?: number
  max?: number
  size?: number
}

export default function Slider({
  value,
  onChange,
  min = 0,
  step = 1,
  max = 100,
  size = 28,
  ...rest
}: InputSliderProps) {
  const changeCallback = useCallback(
    (e) => {
      onChange(parseInt(e.target.value))
    },
    [onChange]
  )

  return (
    <StyledRangeInput
      size={size}
      {...rest}
      type="range"
      value={value}
      style={{ padding: '15px 0' }}
      onChange={changeCallback}
      aria-labelledby="input slider"
      step={step}
      min={min}
      max={max}
    />
  )
}

const SliderWrap = styled.div`
  width: 100%;
  padding: 4px 0;
  position: relative;
  cursor: pointer;
`
const SliderTrack = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 8px;
  border-radius: 99px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.bg03};
`
const SliderFilledTrack = styled.div<{ fillWidth: number }>`
  position: absolute;
  height: 100%;
  left: 0;
  width: ${({ fillWidth }) => fillWidth}px;
  background-color: ${({ theme }) => theme.primary1};
`
const SliderThumb = styled.div<{ left: number; dragging?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: solid 3px ${({ theme }) => theme.white};
  background-color: ${({ theme }) => theme.primary1};
  // background-color: transparent;
  cursor: pointer;
  left: ${({ left }) => left}px;
  &:hover {
    // transform: translateY(-50%) scale(1.25);
  }
`
//将一个数value在两个范围之间映射
function mapValue(value: number, min1: number, max1: number, min2: number, max2: number) {
  return ((value - min1) / (max1 - min1)) * (max2 - min2) + min2
}
//x截取a-b之间
function clamp(x: number, a: number, b: number) {
  return Math.max(a, Math.min(b, x))
}

interface ProgressProps {
  value: number
  onChange(n: number): void
  min?: number
  max?: number
}
export const Progress = ({ value, onChange, min = 0, max = 100 }: ProgressProps) => {
  const barRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)

  const progressRef = useRef(0)
  const [isDragging, setIsDragging] = useState(false)
  const [fillWidth, setFillWidth] = useState(0)
  const [thumbLeft, setThumbLeft] = useState(0)

  useEffect(() => {
    const barWidth = barRef.current?.offsetWidth || 0
    const thumbWidth = thumbRef.current?.offsetWidth || 0
    const pp = value / 100
    //设置填充宽度，并添加滑块偏移
    setFillWidth(barWidth * pp + thumbWidth * (0.5 - pp))
    //设置滑块偏移，减去本身宽度，防止超出
    setThumbLeft((barWidth - thumbWidth) * pp)
  }, [value])

  const updateProgress = useCallback(
    (x: number) => {
      const barWidth = barRef.current?.offsetWidth || 0
      const v = mapValue(x, 0, barWidth, min, max)
      const r = clamp(v, min, max)
      progressRef.current = r

      //更新视图
      const thumbWidth = thumbRef.current?.offsetWidth || 0
      const pp = Math.floor(mapValue(r, min, max, 0, 1) * 100) / 100 //百分比进度

      //设置填充宽度，并添加滑块偏移
      setFillWidth(barWidth * pp + thumbWidth * (0.5 - pp))
      //设置滑块偏移，减去本身宽度，防止超出
      setThumbLeft((barWidth - thumbWidth) * pp)
      onChange(Math.floor(pp * 100))
    },
    [max, min, onChange]
  )
  // 处理滑动
  const handleMouseDown = useCallback(
    (event: any) => {
      if (event.button === 0) {
        if (event.target !== thumbRef.current) {
          //如果没有点到滑块则直接设置进度
          updateProgress(event.nativeEvent.offsetX)
          // console.log('////down-1', event.nativeEvent.offsetX, event)
        } else {
          //点到滑块，设置拖拽状态
          setIsDragging(true)
          // console.log('////down-2')
        }
      }
    },
    [updateProgress]
  )

  const handleMouseMove = useCallback(
    (event: any) => {
      if (event.buttons === 0 || event.button !== 0) {
        // console.log('////move-1', event.buttons, event.button)
        //如果没有按下左键则重置
        setIsDragging(false)
        return
      }
      if (isDragging) {
        //如果鼠标在滑块上重新计算坐标
        if (event.target === thumbRef.current) {
          // console.log('////move-2', event.nativeEvent.offsetX)
          updateProgress(event.nativeEvent.offsetX + thumbRef.current?.offsetLeft)
        } else {
          // console.log('////move-3', event.nativeEvent.offsetX, event.target?.offsetLeft, event)
          updateProgress(event.nativeEvent.offsetX)
        }
      }
    },
    [isDragging, updateProgress]
  )
  const onDragStart = (e: any) => {
    e.preventDefault()
  }
  const onDragOver = (e: any) => {
    e.preventDefault()
  }
  const onDrop = (e: any) => {
    e.preventDefault()
  }

  return (
    <SliderWrap
      ref={barRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <SliderTrack>
        <SliderFilledTrack
          ref={fillRef}
          fillWidth={fillWidth}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      </SliderTrack>
      <SliderThumb ref={thumbRef} left={thumbLeft} onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} />
    </SliderWrap>
  )
}
